import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

const LAST_UPDATED = "December 2026";

const PRIVACY = {
  kind: "privacy",
  title: "Privacy Policy",
  eyebrow: "Legal",
  intro:
    "Poly Grid is built for developers. We try to collect as little as possible, store nothing we don't need, and keep your terminal data on your machine. This policy explains exactly what that means in practice.",
  sections: [
    {
      h2: "What this policy covers",
      body: "This policy covers (1) the polygrid.app marketing website and (2) the Poly Grid macOS application. Where the two differ, we say so explicitly.",
    },
    {
      h2: "What the website collects",
      body: "We use privacy-respecting analytics to measure page views and aggregate referrers. We do not set advertising cookies, sell data, or share visitor data with third parties beyond our analytics provider. If you reach the site with a Do Not Track header set, analytics are skipped.",
    },
    {
      h2: "What the app collects",
      body: "On first launch and during license activation, the app sends a request to our license server containing a randomly generated machine identifier, the app version, and your license key. We use this to enforce the 3-machine activation limit. We do not collect your shell history, terminal output, command input, working directories, environment variables, or filesystem contents.",
    },
    {
      h2: "Crash reports",
      body: "If the app crashes, you'll be asked once whether you want to send anonymous crash reports. Crash reports include the stack trace, the app version, your macOS version, and the active layout (e.g. '2×2'). They do not include any terminal content, pane labels, working directories, or environment variables. You can disable crash reports at any time in Settings → Privacy.",
    },
    {
      h2: "MCP server data",
      body: "The Pro MCP server runs locally on 127.0.0.1 only. It does not send pane content anywhere. Pane content is only exposed to MCP clients you explicitly authorize (Claude Desktop, Cursor, Cline, or another client of your choosing) via a 32-byte bearer token you control. We have no visibility into your MCP traffic.",
    },
    {
      h2: "Payment data",
      body: "License purchases are processed by a third-party payment processor. We never see or store your card details. We receive a confirmation event containing your email, the purchase amount, and a transaction ID — we use these only to issue and validate your license.",
    },
    {
      h2: "Email",
      body: "If you email us (e.g. for support or refunds), we keep the thread for as long as needed to help you and for at most 24 months after the last reply. We never add support emails to a marketing list.",
    },
    {
      h2: "Your rights",
      body: "You can request a copy of, or deletion of, any personal data we hold about you by emailing support@poly-grid.com. We will respond within 30 days. Deleting your data will deactivate your license — you can re-activate by emailing us with proof of purchase.",
    },
    {
      h2: "Changes",
      body: "If this policy changes materially, we'll update the 'Last updated' date at the top and, for active license holders, send a one-time email notification.",
    },
    {
      h2: "Contact",
      body: "support@poly-grid.com — we read every message.",
    },
  ],
};

const TERMS = {
  kind: "terms",
  title: "Terms of Service",
  eyebrow: "Legal",
  intro:
    "By downloading or using Poly Grid you agree to these terms. They're written in plain English on purpose. If anything's unclear, email support@poly-grid.com and we'll explain.",
  sections: [
    {
      h2: "License",
      body: "Poly Grid is sold under a one-time, lifetime, personal-use license. The Free tier is free to use forever. A paid license unlocks Pro features and is valid for activation on up to three (3) machines simultaneously. You may deactivate a machine to free up a slot. Licenses are not transferable to other people, but you are free to move your activation between your own machines.",
    },
    {
      h2: "What you can do",
      body: "Use Poly Grid for any work — personal, commercial, client work, internal tooling — without additional fees per project, per seat, or per output. Modify your own copy of any exported preset JSON and share it with anyone.",
    },
    {
      h2: "What you can't do",
      body: "Resell, redistribute, or sublicense the Poly Grid binary itself. Reverse-engineer the license server. Use the MCP server to facilitate unauthorized access to systems you don't own or aren't permitted to control.",
    },
    {
      h2: "Trial",
      body: "Every install starts with a 7-day Pro trial. After it expires, Free features keep working forever and Pro features open the activation modal instead of running. No nag, no degraded mode during the trial.",
    },
    {
      h2: "Refunds",
      body: "We offer no-questions refunds within 30 days of purchase. Email support@poly-grid.com with your license key. Your license deactivates on refund.",
    },
    {
      h2: "Updates",
      body: "Your one-time payment includes all v1.x updates. Major version upgrades (v2.x, v3.x) are not guaranteed to be free. If we ship a paid major version, existing license holders get a meaningful discount or grandfathering window — we'll announce the policy at the time.",
    },
    {
      h2: "Warranty",
      body: "Poly Grid is provided 'as is' without warranty of any kind. We make no guarantee that the software will be uninterrupted, error-free, or compatible with every future version of macOS. We do our best, but software is software.",
    },
    {
      h2: "Limitation of liability",
      body: "To the maximum extent permitted by law, Poly Grid and its authors are not liable for any incidental, consequential, or indirect damages — including but not limited to lost data, lost work, lost productivity, or lost revenue — arising out of or related to your use of the software. Our total liability under these terms will not exceed the amount you paid for your license.",
    },
    {
      h2: "Termination",
      body: "We may terminate a license for material breach of these terms — most commonly, attempting to circumvent the activation limit or redistributing the binary. We'll email you first. The Free tier continues to work in either case.",
    },
    {
      h2: "Changes",
      body: "We may update these terms. Material changes are announced via email to active license holders and a notice on the site. Continued use after the change date constitutes acceptance.",
    },
    {
      h2: "Contact",
      body: "support@poly-grid.com — we read every message.",
    },
  ],
};

export const Legal = ({ kind }) => {
  const doc = kind === "terms" ? TERMS : PRIVACY;
  const altKind = doc.kind === "privacy" ? "terms" : "privacy";
  const altTitle = doc.kind === "privacy" ? "Terms of Service" : "Privacy Policy";

  useDocumentMeta({
    title: `${doc.title} — Poly Grid`,
    description: doc.intro,
    path: doc.kind === "privacy" ? "/privacy" : "/terms",
  });

  return (
    <div className="relative min-h-screen bg-[var(--pg-bg)]">
      <Navbar />

      <section
        data-testid={`legal-page-${doc.kind}`}
        className="border-b border-pg"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 tag-label text-[var(--pg-text-dim)] hover:text-white transition-colors mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>

          <div className="tag-label text-cyan-accent">/// {doc.eyebrow}</div>
          <h1 className="font-display mt-3 text-4xl lg:text-6xl font-black tracking-tighter uppercase">
            {doc.title}
          </h1>
          <div className="mt-4 tag-label text-[var(--pg-text-muted)]">
            Last updated · {LAST_UPDATED}
          </div>

          <p className="mt-8 text-[16px] text-[var(--pg-text-dim)] leading-relaxed">
            {doc.intro}
          </p>

          <div className="mt-12 space-y-10">
            {doc.sections.map((s, i) => {
              const anchor = s.h2.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <section
                  key={i}
                  id={anchor}
                  className="scroll-mt-24"
                  data-testid={`legal-section-${anchor}`}
                >
                  <h2 className="font-display text-xl lg:text-2xl font-bold tracking-tight">
                    <a
                      href={`#${anchor}`}
                      className="hover:text-cyan-accent transition-colors group"
                    >
                      <span className="text-cyan-accent mr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        #
                      </span>
                      {s.h2}
                    </a>
                  </h2>
                  <p className="mt-3 text-[15px] leading-relaxed text-[var(--pg-text-dim)]">
                    {s.body}
                  </p>
                </section>
              );
            })}
          </div>

          <div className="mt-16 pt-8 border-t border-pg flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div className="text-xs font-mono text-[var(--pg-text-muted)]">
              Questions? support@poly-grid.com
            </div>
            <Link
              to={`/${altKind}`}
              data-testid={`legal-link-${altKind}`}
              className="tag-label text-[var(--pg-text-dim)] hover:text-white border border-pg px-4 py-2 hover:bg-pg-surface transition-colors"
            >
              Read {altTitle} →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const Privacy = () => <Legal kind="privacy" />;
export const Terms = () => <Legal kind="terms" />;

export default Legal;
